import classNames from "classnames";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Loader from "../Loader";

import { avatarResizedKey } from "@const/avatar";

import { ReactComponent as IconAvatar } from "../../assets/ic-avatar-placeholder.svg";
import { ReactComponent as IconAvatarBig } from "../../assets/ic_user placeholder_big.svg";
import { ReactComponent as IconGhost } from "../../assets/ic-ghost.svg";

import styles from "./index.module.scss";
import { useNavigate } from "react-router-dom";
import routes from "@const/routes";

const placeholderClassNames =
  "d-flex align-items-center justify-content-center";

/**
 * Move item from 1 pos to another
 * @param {string} avatar
 * @param {string} className
 * @param {string} iconClassName
 * @param {boolean} isDeleted
 * @param {function} onClick
 * @param {boolean} isBigAvatar
 * @param {boolean} showLiveBadge
 * @param {number} isAvatarLoading
 * @param {boolean} thickBorder
 * @param {Object} style
 * 'live' | 'moderator' | 'goldTipper' | 'silverTipper' | 'bronzeTipper'
 * @param {string} type
 * @param {object} activeStream
 */

const Avatar = ({
  avatar,
  className,
  iconClassName,
  isDeleted,
  onClick,
  isBigAvatar,
  isAvatarLoading,
  type,
  showLiveBadge,
  style,
  thickBorder,
  activeStream,
  avatarLiveBadgeClassname,
}) => {
  const navigate = useNavigate();

  const { t } = useTranslation("livestream");
  const [currentSrc, setCurrentSrc] = useState();

  const memAvatar = useMemo(() => avatar, [avatar]);

  useEffect(() => {
    const avatarResized = memAvatar?.resizedImages?.[avatarResizedKey]?.link;

    setCurrentSrc(avatarResized || memAvatar?.link);
  }, [memAvatar]);

  const highlightClassnamesByType = {
    live: styles.avatarLiveImage,
    moderator: styles.avatarModeratorImage,
    goldTipper: styles.avatarGoldTipper,
    silverTipper: styles.avatarSilverTipper,
    bronzeTipper: styles.avatarBronzeTipper,
  };

  const handleOnLiveAvatarClick = (e) => {
    e.preventDefault();
    if (type === "live" && activeStream?.uniqueKey) {
      navigate(`${routes.STREAM()}/${activeStream.uniqueKey}`);
    } else {
      onClick?.();
    }
  };

  const Content = () => (
    <div
      className={classNames(
        "rounded-circle flex-shrink-0 border-none",
        !currentSrc && placeholderClassNames,
        styles.avatarImage,
        className,
        type && styles.avatarHighligthedImage,
        type && highlightClassnamesByType[type],
      )}
      style={style}
    >
      {!currentSrc && <div className={styles.placeholder} />}

      {isAvatarLoading ? (
        <div className="h-100 w-100 d-flex position-absolute justify-content-center align-items-center top-0">
          <Loader />
        </div>
      ) : null}
      {isDeleted ? (
        <IconGhost className="z-index-2" />
      ) : currentSrc ? (
        <img
          alt="avatar"
          src={currentSrc}
          className={classNames(
            "z-index-2 img w-100 h-100",
            thickBorder && "thick-border",
          )}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            setCurrentSrc(null);
          }}
          onClick={onClick}
        />
      ) : isBigAvatar ? (
        <IconAvatarBig
          className={classNames("z-index-2", iconClassName)}
          onClick={onClick}
        />
      ) : (
        <IconAvatar
          className={classNames("z-index-2", iconClassName)}
          onClick={onClick}
          width="20"
          height="20"
        />
      )}
    </div>
  );

  return type === "live" ? (
    <div
      className={classNames(styles.avatarHighlightContainer, className)}
      onClick={handleOnLiveAvatarClick}
    >
      <Content />
      {showLiveBadge && (
        <div
          className={classNames(
            styles.avatarLiveBadge,
            avatarLiveBadgeClassname,
            "text-regular",
          )}
        >
          {t("LIVE")}
        </div>
      )}
    </div>
  ) : (
    <Content />
  );
};

export default memo(Avatar);
